import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import toast from "react-hot-toast";
import Swal from "sweetalert2";
import "sweetalert2/dist/sweetalert2.min.css";
import "./editDraft.css";
import DraftReportForm from "../../../components/lecturer/draftForm/draftForm";
import { FileText, Users, MessageSquare, BookOpen } from "lucide-react";

const EditDraft = () => {
  const API_ENDPOINT = process.env.REACT_APP_API_END_POINT;
  const { draftId } = useParams();
  const nav = useNavigate();
  const [selectedChannels, setSelectedChannels] = useState({});
  const [channels, setChannels] = useState([]);
  const [groups, setGroups] = useState([]);
  const [sessionFiles, setSessionFiles] = useState([]);
  const [showReportPreview, setShowReportPreview] = useState(false);
  const [attendanceRegisterFileDto, setAttendanceRegisterFileDto] =
    useState([]);
  const steps = [
    { number: 1, title: "Module & Dates", icon: BookOpen },
    { number: 2, title: "Weekly Report", icon: FileText },
    { number: 3, title: "Communication", icon: MessageSquare },
    { number: 4, title: "Sessions", icon: Users },
  ];
  const [currentStep, setCurrentStep] = useState(1);
  const [reportData, setReportData] = useState({
    modules: [],
    moduleIds: [],
    startDate: "",
    endDate: "",
    suggestions: "",
    challenges: "",
    channels: [],
    channelActivities: {},
    weeklyActivity: "",
    sessions: [],
    hasAttendanceRegister: false,
    attendanceRegisterFileDto: [],
  });

  //Drafts
  const getDraft = useCallback(
    async (draftId) => {
      try {
        const response = await axios.get(
          `${API_ENDPOINT}/api/LecturerReports/ViewMyDraft`,
          {
            params: { draftId: draftId },
            withCredentials: true,
            headers: { "Content-Type": "application/json" },
            validateStatus: () => true,
          },
        );

        if (response?.data?.status) {
          const draftModules = response.data.modules || [];
          const mergedChannelActivities = {};
          const draftSessions = response.data.sessions || [];

          response.data.channelActivities.forEach(
            ({ channelName, activity }) => {
              if (mergedChannelActivities[channelName]) {
                mergedChannelActivities[channelName] += `\n${activity}`;
              } else {
                mergedChannelActivities[channelName] = activity;
              }
            }
          );
          let channelList = response.data.channelActivities || [];
          if (Array.isArray(channelList) && channelList.length > 0) {
            if (typeof channelList[0] === "string") {
              channelList = channelList.map((name, idx) => ({
                channelName: name,
              }));
            }
          }

          const summaries = response.data.channelActivities || {};
          const normalizedSummaries = {};
          channelList.forEach((c) => {
            const channelName = c.channelName;
            const summaryKey = Object.keys(summaries).find(
              (k) => k.trim().toLowerCase() === channelName.trim().toLowerCase()
            );
            normalizedSummaries[channelName] = summaryKey
              ? summaries[summaryKey]
              : "";
          });

          setReportData({
            modules: response.data.modules,
            moduleIds: draftModules.map((m) => m.moduleId),
            startDate: response.data.startDate,
            endDate: response.data.endDate,
            suggestions: response.data.suggestions,
            challenges: response.data.challenges,
            channels: channelList,
            channelActivities: normalizedSummaries,
            weeklyActivity: response.data.weeklyActivity,
            sessions: draftSessions.map((s, i) => ({
              sessionId: s.sessionId,
              sessionNumber: s.sessionNumber || i + 1,
              sessionDate: s.sessionDate,
              numberOfStudents: s.numberOfStudents,
              groupId: s.group.groupId,
            })),
            hasAttendanceRegister: response.data.hasAttendanceRegister,
            attendanceRegisterFileDto:
              response.data.attendanceRegisterFileDto || [],
          });
          const initialSelectedChannels = {};
          if (
            response.data.channelActivities &&
            response.data.channelActivities.length > 0
          ) {
            response.data.channelActivities.forEach((ca) => {
              initialSelectedChannels[ca.channelName] = {
                show: true,
                content: ca.activity,
              };
            });
          }
          setSelectedChannels(initialSelectedChannels);
          setAttendanceRegisterFileDto(response.data.attendanceRegisterFileDto || []);
          console.log(response.data.message);
        } else {
          console.log(response.data.message);
        }
      } catch (error) {
        console.log(error.response?.data?.message || "An error occurred.");
      }
    },
    [API_ENDPOINT]
  );
  const getCommunicationChannel = useCallback(async () => {
    try {
      const response = await axios.get(
        `${API_ENDPOINT}/api/AcademyGet/GetAllCommunicationChannels`,
        {
          withCredentials: true,
          headers: { "Content-Type": "application/json" },
        }
      );
      console.log("Channels API response:", response.data);
      if (response?.data?.status) {
        setChannels(response.data.channels);
      } else {
        toast.error(response.data.message || "Failed to load channels");
      }
    } catch (error) {
      console.error("Error fetching channels:", error);
      toast.error(error.response?.data?.message || "An error occurred");
    }
  }, [API_ENDPOINT]);
  useEffect(() => {
    getCommunicationChannel();
    if (draftId) {
      getDraft(draftId);
    }
  }, [draftId, getCommunicationChannel, getDraft]);

  // Load groups when modules are selected
  // Load groups when selected modules change
  useEffect(() => {
    if (reportData.moduleIds && reportData.moduleIds.length > 0) {
      getModuleGroups(reportData.moduleIds);
    } else {
      setGroups([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- getModuleGroups is stable, only re-run when moduleIds change
  }, [reportData.moduleIds]);

  //Deleting uploaded files method  
  const handleDeleteFile = async (attendanceRegisterId) => {
    console.log("Deleting file:", attendanceRegisterId);
    try {
      const response = await axios.delete(
        `${API_ENDPOINT}/api/LecturerReports/DeleteDraftAttendanceRegister?attendanceRegisterId=${attendanceRegisterId}`,
        {
          withCredentials: true,
          headers: { "Content-Type": "application/json" },
        }
      );

      if (response.data?.status === true) {
        // Remove the deleted file from both reportData and attendanceRegisterFileDto state
        setReportData((prevData) => ({
          ...prevData,
          attendanceRegisterFileDto: Array.isArray(prevData.attendanceRegisterFileDto)
            ? prevData.attendanceRegisterFileDto.filter(
                (file) =>
                  (file.AttendanceRegisterId || file.attendanceRegisterId) !== attendanceRegisterId
              )
            : [],
        }));
        setAttendanceRegisterFileDto((prev) =>
          Array.isArray(prev)
            ? prev.filter(
                (file) =>
                  (file.AttendanceRegisterId || file.attendanceRegisterId) !== attendanceRegisterId
              )
            : []
        );
        toast.success("File deleted successfully.");
      } else {
        toast.error(response.data?.message || "Failed to delete the file.");
      }
    } catch (error) {
      console.error("Error deleting file:", error);
      toast.error("An error occurred while deleting the file.");
    }
  };


  const updateDraft = async (e) => {
    e.preventDefault();

    const channelActivities = Object.entries(selectedChannels)
      .filter(([_, value]) => value.show && value.content.trim())
      .map(([channelName, value]) => ({
        channelName,
        activity: value.content,
      }));

    const formData = new FormData();
    formData.append("startDate", reportData.startDate);
    formData.append("endDate", reportData.endDate);
    formData.append("suggestions", reportData.suggestions || "");
    formData.append("challenges", reportData.challenges || "");
    formData.append("weeklyActivity", reportData.weeklyActivity || "");

    (reportData.moduleIds || []).forEach((id, index) => {
      formData.append(`moduleIds[${index}]`, id);
    });

    (channelActivities || []).forEach((activity, index) => {
      formData.append(`channelActivities[${index}].channelName`, activity.channelName);
      formData.append(`channelActivities[${index}].activity`, activity.activity);
    });

    (reportData.sessions || []).forEach((session, index) => {
      formData.append(`sessions[${index}].sessionNumber`, session.sessionNumber);
      formData.append(`sessions[${index}].numberOfStudents`, session.numberOfStudents);
      formData.append(`sessions[${index}].groupId`, session.groupId);
      formData.append(`sessions[${index}].sessionDate`, session.sessionDate);
    });

    sessionFiles.forEach((file) => formData.append("files", file));

    try {
      const uploadConfig = { withCredentials: true };
      const response = await axios.post(
        `${API_ENDPOINT}/api/LecturerReports/CreateDraft`,
        formData,
        {
          ...uploadConfig,
          validateStatus: () => true,
        },
      );

      if (response?.data?.status) {
        const newDraftId = response.data.draftId;

        for (const file of sessionFiles) {
          await uploadDraftAttendanceRegister(newDraftId, file);
        }
        toast.success(response.data.message || "Draft saved successfully!");
        nav("/lecturer-drafts");
      } else {
        toast.error(response.data.message || "Failed to save draft.");
      }
    } catch (error) {
      toast.error(error.response?.data?.message || "Error saving draft.");
    }
  };

  const submitReport = async (e) => {
    e.preventDefault();

    // Check if attendance register exists (either already uploaded or new files selected)
    const hasExistingRegister = reportData.hasAttendanceRegister && 
                                 reportData.attendanceRegisterFileDto && 
                                 reportData.attendanceRegisterFileDto.length > 0;
    const hasNewFiles = sessionFiles && sessionFiles.length > 0;

    if (!hasExistingRegister && !hasNewFiles) {
      Swal.fire({
        icon: "error",
        title: "Attendance Register Required",
        text: "You cannot submit a report without uploading an attendance register. Please upload an attendance register file before submitting.",
        showConfirmButton: true,
        customClass: { popup: "my-swal-theme" },
      });
      return;
    }

    const channelActivities = Object.entries(selectedChannels)
      .filter(([_, value]) => value.show && value.content.trim())
      .map(([channelName, value]) => ({
        channelName,
        activity: value.content,
      }));

    // Upload any new attendance register files to the draft before submission
    if (hasNewFiles) {
      for (const file of sessionFiles) {
        await uploadDraftAttendanceRegister(draftId, file);
      }
    }

    const formData = new FormData();
    formData.append("startDate", reportData.startDate);
    formData.append("endDate", reportData.endDate);
    formData.append("suggestions", reportData.suggestions || "");
    formData.append("challenges", reportData.challenges || "");
    formData.append("weeklyActivity", reportData.weeklyActivity || "");
    formData.append("draftId", draftId);

    (reportData.moduleIds || []).forEach((id, index) => {
      formData.append(`moduleIds[${index}]`, id);
    });

    (channelActivities || []).forEach((activity, index) => {
      formData.append(`channelActivities[${index}].channelName`, activity.channelName);
      formData.append(`channelActivities[${index}].activity`, activity.activity);
    });

    (reportData.sessions || []).forEach((session, index) => {
      formData.append(`sessions[${index}].sessionNumber`, session.sessionNumber);
      formData.append(`sessions[${index}].numberOfStudents`, session.numberOfStudents);
      formData.append(`sessions[${index}].groupId`, session.groupId);
      formData.append(`sessions[${index}].sessionDate`, session.sessionDate);
    });

    try {
      const uploadConfig = { withCredentials: true };
      const response = await axios.post(
        `${API_ENDPOINT}/api/LecturerReports/SubmitReport`,
        formData,
        {
          ...uploadConfig,
          validateStatus: () => true,
        },
      );

      if (response?.data?.status) {
        Swal.fire({
          icon: "success",
          title: "Success",
          text: response.data.message,
          showConfirmButton: true,
          customClass: { popup: "my-swal-theme" },
        });
        nav("/report-history");
      } else {
        Swal.fire({
          icon: "error",
          title: "Error",
          text: response.data.message || "Failed to submit report.",
          showConfirmButton: true,
          customClass: { popup: "my-swal-theme" },
        });
      }
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Failed!",
        text: error.response?.data?.message || "An unexpected error occurred.",
        showConfirmButton: true,
        customClass: { popup: "my-swal-theme" },
      });
    }
  };

  const uploadDraftAttendanceRegister = async (draftId, file) => {
    const uploadConfig = { withCredentials: true };
    try {
      const base64 = await new Promise((resolve) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result.split(",")[1]);
        reader.readAsDataURL(file);
      });
      const response = await axios.post(
        `${API_ENDPOINT}/api/LecturerReports/UploadDraftAttendanceRegister`,
        { reportId: parseInt(draftId), fileName: file.name, contentType: file.type, fileData: base64 },
        uploadConfig
      );
      if (response?.data?.status) setAttendanceRegisterFileDto(response.data.attendanceRegisterFileDto || []);
      return response;
    } catch (error) {
      throw error;
    }
  };


  const getModuleGroups = async (moduleIds) => {
    try {
      const response = await axios.post(
        `${API_ENDPOINT}/api/LecturerAcademy/GetlecturerModuleGroups`,
        { moduleIds },
        {
          withCredentials: true,
          headers: { "Content-Type": "application/json" },
        }
      );
      if (response?.data?.status) {
        console.log(
          "This are the groups for the selected module" +
          " " +
          response?.data?.groups
        );
        setGroups(response.data.groups);
      } else {
        console.log(response.data.message);
      }
    } catch (error) {
      console.error(error);
    }
  };

  //communication channels and activities
  const handleChannelSelection = (channelName) => {
    setSelectedChannels((prev) => ({
      ...prev,
      [channelName]: !prev[channelName]
        ? { show: true, content: "" }
        : { ...prev[channelName], show: !prev[channelName].show },
    }));
  };
  const updateChannelActivity = (channelName, value) => {
    setSelectedChannels((prev) => ({
      ...prev,
      [channelName]: {
        ...prev[channelName],
        content: value,
      },
    }));
  };

  //handle sessions
  const addSession = () => {
    const newSession = {
      sessionNumber: 1,
      numberOfStudents: "",
      groupId: 0,
      sessionDate: "",
    };

    setReportData((prev) => ({
      ...prev,
      sessions: [...prev.sessions, newSession],
    }));
  };

  const updateSession = (index, field, value) => {
    setReportData((prev) => {
      const updatedSessions = [...prev.sessions];
      const sessionToUpdate = { ...updatedSessions[index], [field]: value };

      if (field === "groupId" && value !== 0) {
        const sameGroupSessions = updatedSessions.filter(
          (s, i) => s.groupId === value && i !== index
        );

        sessionToUpdate.sessionNumber = sameGroupSessions.length + 1;
      }

      updatedSessions[index] = sessionToUpdate;

      return {
        ...prev,
        sessions: updatedSessions,
      };
    });
  };

  const removeSession = (index) => {
    setReportData((prev) => ({
      ...prev,
      sessions: prev.sessions.filter((_, i) => i !== index),
    }));
  };

  //Navigation between the steps
  const canProceedToNext = () => {
    switch (currentStep) {
      case 1:
        return (
          reportData.moduleIds.length > 0 &&
          reportData.startDate &&
          reportData.endDate
        );
      case 2:
        return reportData.weeklyActivity.trim();
      case 3:
        return Object.values(selectedChannels).every(
          (val) => !val.show || (val.content && val.content.trim())
        );
      case 4:
        return (
          (reportData.sessions.length > 0 &&
            reportData.sessions.every(
              (s) => s.groupId > 0 && s.numberOfStudents > 0 && s.sessionDate
            ) &&
            sessionFiles !== null) ||
          reportData.hasAttendanceRegister
        );
      default:
        return false;
    }
  };

  const handleNextStep = () => {
    if (currentStep === 1) {
      getModuleGroups(reportData.moduleIds);
    }
    if (currentStep === 2) {
      if (!reportData.weeklyActivity.trim()) {
        toast.error("Please fill in the Weekly Activity before proceeding.");
        return;
      }
    }

    if (currentStep < 4) {
      setCurrentStep((prev) => prev + 1);
    }
  };

  return (
    <div>
      <DraftReportForm
        reportType={"Draft"}
        steps={steps}
        currentStep={currentStep}
        setCurrentStep={setCurrentStep}
        reportData={reportData}
        setReportData={setReportData}
        getModuleGroups={getModuleGroups}
        communicationChannels={channels}
        selectedChannels={selectedChannels}
        handleChannelSelection={handleChannelSelection}
        updateChannelActivity={updateChannelActivity}
        addSession={addSession}
        removeSession={removeSession}
        updateSession={updateSession}
        availableGroups={groups}
        canProceedToNext={canProceedToNext}
        handleNext={handleNextStep}
        sessionFiles={sessionFiles}
        setSessionFiles={setSessionFiles}
        setShowReportPreview={setShowReportPreview}
        showReportPreview={showReportPreview}
        submitReport={submitReport}
        saveDraft={updateDraft}
        attendanceRegisterFileDto={attendanceRegisterFileDto}
        handleDeleteFile={handleDeleteFile}
      />

    </div>
  );
};

export default EditDraft;
